import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { DetailViewCard } from '../../../../Compounds/CardWithTitle';
import { format } from 'date-fns';
import { BasicDataGrid } from '../../../../Compounds/Table/BasicDataGrid';
import { PortalUserData } from '../../../../../types';
import { DetailCTARenderer } from '../../../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { Permission, updateState } from '../../../../../store/GeneralStore';
import { Link } from 'react-router-dom';
import { adminUrlForRole } from '../../../../../store/User';
import { Icon } from '../../../../Atoms/Icon';
import { TextSpan } from '../../../../Atoms/Text';
import { PermissibleRender } from '../../../../Compounds/PermissibleRender';
import { Switch } from '@mui/material';
import { putPortalUser } from '../../../../../Api/Admin/PortalUsers/putPortalUser';
import { getPortalUserSubject, withAccountSfId } from '../../../../../store/PortalUser';
import { useSnackbar } from '../../../../../Context/SnackbarContext';
import { useState } from 'react';

export interface UsersParams {
  users: PortalUserData[];
}
export const Users = ({ users }: UsersParams) => {
  const { setSnack } = useSnackbar();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  const handleMfaToggle = async (user: PortalUserData) => {
    setTogglingIds((prev) => new Set(prev).add(user.id));
    const saving = await withAccountSfId({ ...user, otp_required_for_login: !user.otp_required_for_login });
    const { data, error } = saving.error ? saving : await putPortalUser(saving.data);

    if (error) {
      setSnack({ message: error.message, type: 'error', open: true });
    }

    if (data) {
      const updated = getPortalUserSubject()
        .getValue()
        .map((u) =>
          u.id === data.id ? { ...data, rolesDisplay: data.roles?.map((r) => r?.name).join(', ') || '' } : u
        );
      getPortalUserSubject().next(updated);
    }

    setTogglingIds((prev) => {
      const next = new Set(prev);
      next.delete(user.id);
      return next;
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'details',
      headerName: '',
      sortable: false,
      width: 65,
      filterable: false,
      renderCell: (params: GridCellParams) => {
        const onClick = () => updateState({ admin: { selectedTab: 0 } });
        return (
          <DetailCTARenderer
            data={params}
            value={''}
            valueFormatted={''}
            onClick={onClick}
          />
        );
      },
      hideable: false,
      disableReorder: true
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      valueGetter: (row, value) => `${value.first_name} ${value.last_name}`
    },
    {
      field: 'updated_at',
      headerName: 'Last Login',
      sortable: true,
      valueFormatter: (value) => {
        return value ? format(value, 'PPpp') : '';
      },
      flex: 1
    },
    {
      field: 'otp_required_for_login',
      headerName: 'MFA',
      sortable: true,
      filterable: true,
      width: 100,
      renderCell: (params: GridCellParams) => (
        <Switch
          checked={!!params.row.otp_required_for_login}
          onChange={() => handleMfaToggle(params.row as PortalUserData)}
          disabled={togglingIds.has(params.row.id)}
          size="small"
        />
      )
    }
  ];

  const userActions = () => {
    const state = {
      prevpath: location.pathname
    };

    return (
      <PermissibleRender requiredPermissions={[Permission.CREATE_USERS]}>
        <Link
          role="link"
          title="New User"
          className="flex space-x-2 items-center"
          data-cy="account-details-new-user"
          state={state}
          to={`${adminUrlForRole()}/new-user`}>
          <Icon
            type="plus"
            className="p-2"
          />
          <TextSpan
            color="grey12"
            size="sm14">
            New User
          </TextSpan>
        </Link>
      </PermissibleRender>
    );
  };

  return (
    <DetailViewCard
      title="Users"
      enableCollapse
      collapsed
      cardActions={userActions()}>
      <BasicDataGrid
        rows={users}
        columns={columns}
        className="h-96 overflow-auto"
      />
    </DetailViewCard>
  );
};
